import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { UserContext } from 'context/User/UserContext';
import {
	getSecondaryEmails,
	getUserWorkItemsByEmail,
	getWorkItems,
} from './_api';
import { debounce } from '../../../utils/misc/debounce';
import { raiseErrorBanner } from 'components/MessageDispatch/messageDispatch';
import { prettyDateFormat } from 'utils';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from '../../../components/layouts/theme';
import Base from 'pages/Base/Base';
import Box from '@mui/material/Box';
import StyleAuxiliary from 'hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import { DataTable } from 'components/UI/MDC/DataTable/DataTable';
import { Subtitle1 } from 'components/UI/MDC/Typography/Typography';
import { Tooltip } from 'components/UI/MDC/Tooltip/Tooltip';
import { CONTENT_ALIGNMENT } from 'components/UI/MDC/Tooltip/_types';
import { Actions, ActionIcons } from 'components/UI/MDC/Card/Card';
import BaseSpeedDial, {
	SpeedDialActionProps,
	SpeedDialProps,
} from '../../../components/UI/SpeedDial/BaseSpeedDial';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import LogoDevIcon from '@mui/icons-material/LogoDev';
import { CSS_CLASSES } from './_constants';

import classes from './WorkItemTracking.module.scss';

const WorkItemTracking = () => {
	const dispatch = useDispatch();
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;

	const [sRendered, setRendered] = useState(false);
	const [sRows, setRows] = useState([]);
	const [sEmails, setEmails] = useState([]);
	const [sSelectedEmail, setSelectedEmail] = useState();

	/**
	 * Hook intended to execute only once. Saves email of logged-in user, triggering initial retrieval of work items.
	 */
	useEffect(() => {
		if (user) {
			setSelectedEmail(user.Email?.toLowerCase());
			setEmails([]);
			setRendered(false);
		}
	}, [user]);

	/**
	 * Retrieves work items when page is loading and an email is available to search against.
	 */
	useEffect(() => {
		if (sSelectedEmail && !sRendered) {
			getRows(sSelectedEmail);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sSelectedEmail, sRendered]);

	/**
	 * Queries Azure DevOps for work items with references to email of interest.
	 * If references exist, evaluates presence of Test Case relations to better determine status of items before returning.
	 *
	 * @param {string} emailUser email address to search against.
	 * @returns {void}
	 */
	const getRows = async emailUser => {
		if (sRendered) {
			setRendered(false);
		}
		try {
			const arrWIReferences = await getUserWorkItemsByEmail(
				emailUser,
				dispatch
			);
			let arrWIDetails = await getWorkItems(arrWIReferences);

			if(arrWIDetails.length < 1){
				const arrEmails = await getSecondaryEmails(user.UPN, dispatch);
				if(arrEmails.length > 1){
					setEmails(arrEmails);
				}
			}
			setRows(prepareRows(arrWIDetails));
		} catch (e) {
			raiseErrorBanner(e, dispatch);
		} finally {
			setRendered(true);
		}
	};

	const prepareRows = arrWorkItemDetails => {
		let arrRows = [];
		if (arrWorkItemDetails?.length > 0) {
			arrRows = arrWorkItemDetails.map((wi, index) => {
				const targetUTC = wi.Fields[
					'Microsoft.VSTS.Scheduling.TargetDate'
				]
					? new Date(
							wi.Fields['Microsoft.VSTS.Scheduling.TargetDate'],
						)
					: null;
				const target = targetUTC ? new Date(targetUTC.getUTCFullYear(), targetUTC.getUTCMonth(), targetUTC.getUTCDate()) : null;
				return {
					Key: index.toString(),
					ActionCells: [],
					Cells: {
						Title: {
							Value: wi.Fields?.['System.Title'],
							Validation: {
								Key: 'true',
							},
							props: {
								extendtext: 45,
							},
						},
						Status: {
							Value: getStatus(wi.Fields?.['System.State']),
							Validation: {
								Key: 'true',
							},
							props: {
								className: getStatusStyle(
									wi.Fields?.['System.State'],
								),
							},
						},
						Target: {
							Value: target ? prettyDateFormat(target) : '',
							Validation: {
								Key: 'true',
							},
						},
					},
				};
			});
		}
		return arrRows;
	};

	const getStatus = status => {
		let displayStatus;
		let textHover = '';
		switch (status) {
			case 'New':
				textHover = 'Work has not started.';
				break;
			case 'Active':
				textHover = 'Work is underway.';
				break;
			case 'Resolved':
			case 'Peer Review':
			case 'DBA Review':
				textHover = 'Work is in peer review.';
				displayStatus = 'Resolved'; 
				break;
			case 'QA Review':
			case 'Stakeholder Review':
				textHover = 'Ready for your review in our TEST site.';
				displayStatus = (
					<a
						href='https://imstest.upmc.com'
						rel='noopener noreferrer'
						target='_blank'
					>
						<u>Ready for Testing</u>
					</a>
				);
				break;
			case 'Closed':
				textHover = 'Work is deployed or staged for next deployment.';
				break;
			default:
				break;
		}
		return (
			<>
				{displayStatus ?? <span>{status}</span>}
				<Tooltip align={CONTENT_ALIGNMENT.BOTTOM} content={textHover}>
					<InfoIcon />
				</Tooltip>
			</>
		);
	};

	const getStatusStyle = status => {
		switch (status) {
			case 'New':
				return classes[CSS_CLASSES.CELL_PROPOSED];
			case 'Active':
				return classes[CSS_CLASSES.CELL_ACTIVE];
			case 'Resolved':
			case 'Peer Review':
			case 'DBA Review':
			case 'QA Review':
			case 'Stakeholder Review':
				return classes[CSS_CLASSES.CELL_RESOLVED];
			case 'Closed':
				return classes[CSS_CLASSES.CELL_CLOSED];
			default:
				return '';
		}
	};

	const getSpeedDialProps = () => {
		let props = new SpeedDialProps();
		props.ariaLabel = 'Submit New Request';
		props.icon = <AddIcon />;
		props.sx = {
			'& .MuiFab-primary': {
				backgroundColor: 'secondary.main',
			},
		};
		props.direction = 'right';
		return props;
	};

	const getSpeedDialActions = () => {
		let arrActions = [];
		let actionAppAdd = new SpeedDialActionProps();
		actionAppAdd.key = '0';
		actionAppAdd.tooltipPlacement = 'bottom';
		actionAppAdd.tooltipTitle = 'New Application';
		actionAppAdd.icon = <NoteAddIcon />;
		actionAppAdd.onClick = debounce(() =>
			window.open(
				'https://forms.office.com/Pages/ResponsePage.aspx?id=Ptc9i3JOeUaxkVbaFYhxK4DUoNwKnN9AmTW4BFQzoIFUMFJXWU42NDlTVVJJTU9CQTYwSFBTRzdYTyQlQCN0PWcu',
				'_blank'
			)
		);
		actionAppAdd.sx.color = null;
		arrActions.push(actionAppAdd);

		let actionAppMod = new SpeedDialActionProps();
		actionAppMod.key = '1';
		actionAppMod.tooltipPlacement = 'bottom';
		actionAppMod.tooltipTitle = 'Application Modification';
		actionAppMod.icon = <EditIcon />;
		actionAppMod.onClick = debounce(() =>
			window.open('https://forms.office.com/r/5eXMS0bnRs', '_blank')
		);
		actionAppMod.sx.color = null;
		arrActions.push(actionAppMod);

		let actionDevAdd = new SpeedDialActionProps();
		actionDevAdd.key = '2';
		actionDevAdd.tooltipPlacement = 'bottom';
		actionDevAdd.tooltipTitle = 'Other Request';
		actionDevAdd.icon = <LogoDevIcon />;
		actionDevAdd.onClick = debounce(() =>
			window.open('https://forms.office.com/r/kwT4p6kBEN', '_blank')
		);
		actionDevAdd.sx.color = null;
		arrActions.push(actionDevAdd);

		return arrActions;
	};

	return (
		<ThemeProvider theme={theme}>
			<Base title='Work Item Tracking'>
				{sEmails.length > 0 && (
					<StyleAuxiliary
						classes={classes[CSS_CLASSES.DIV_SEC_EMAIL]}
					>
						<Subtitle1>
							We noticed you have more than one email. Did you
							submit your request with a different email?
						</Subtitle1>
						<FormControl>
							<InputLabel id='labelEmails'>Email</InputLabel>
							<Select
								labelId='labelEmails'
								id='selectEmails'
								label='Email'
								defaultValue=''
								value={sSelectedEmail}
								onChange={e => {
									setSelectedEmail(e.target.value);
									setRendered(false);
								}}
							>
								{sEmails.map((email, index) => {
									email = email.toLowerCase();
									return (
										<MenuItem key={index} value={email}>
											{email}
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>
					</StyleAuxiliary>
				)}
				<Box
					sx={{
						'& [class*="ExtendText_box"]': {
							justifyContent: 'left',
						},
					}}
				>
					<DataTable
						headers={[
							{ Value: 'Title', props: {} },
							{ Value: 'Status', props: {} },
							{ Value: 'Target', props: {} },
						]}
						label='Your Work Items'
						loading={!sRendered}
						rows={sRows}
					/>
				</Box>
				{sRendered && sRows.length < 1 && (
					<Subtitle1>
						You have no work items in our development queue.
					</Subtitle1>
				)}
				<Actions>
					<BaseSpeedDial
						speedDialProps={getSpeedDialProps()}
						speedDialActions={getSpeedDialActions()}
					/>
					<ActionIcons>
						<Tooltip content='Refresh'>
							<IconButton
								onClick={debounce(() => setRendered(false))}
							>
								<RefreshIcon />
							</IconButton>
						</Tooltip>
					</ActionIcons>
				</Actions>
			</Base>
		</ThemeProvider>
	);
};

WorkItemTracking.displayName = 'WorkItemTracking';

export default WorkItemTracking;
